import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import BrandLoadingScreen from '../components/BrandLoadingScreen';
import { calculateFirstYearPlan, calculateSubsequentYearMonthly } from '../utils/literentMath';



// ─── Set your actual launch date here ────────────────────────────────────────
const LAUNCH_DATE = new Date('2026-05-21T00:00:00');

// ─── Countdown hook ───────────────────────────────────────────────────────────
function useCountdown(targetDate) {
  const calc = () => {
    const diff = targetDate - new Date();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, launched: true };
    return {
      days:     Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours:    Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes:  Math.floor((diff / (1000 * 60)) % 60),
      seconds:  Math.floor((diff / 1000) % 60),
      launched: false,
    };
  };
  const [timeLeft, setTimeLeft] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

function CountdownBox({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-white/15 border border-white/25 rounded-2xl w-20 sm:w-24 h-20 sm:h-24 flex items-center justify-center">
        <span className="text-3xl sm:text-4xl font-extrabold text-white tabular-nums">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-white/70 text-xs sm:text-sm font-medium uppercase tracking-widest mt-2">{label}</span>
    </div>
  );
}

export default function HomePage() {
  const [firstRent, setFirstRent] = useState('');
  const [subsequentRent, setSubsequentRent] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Search state
  const [searchLocation, setSearchLocation] = useState('');
  const [searchBudget, setSearchBudget] = useState('');
  const [searchBedrooms, setSearchBedrooms] = useState('');

  // Featured houses — same API call as BrowsePage
  const [featuredHouses, setFeaturedHouses] = useState([]);
  const [housesLoading, setHousesLoading] = useState(true);
  const [housesError, setHousesError] = useState(null);

  const { isLoggedIn, logout, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const countdown = useCountdown(LAUNCH_DATE);

  const adminToken = localStorage.getItem('adminAuthToken');
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isAdminLoggedIn = !!adminToken && adminUser.role;

  const getDashboardLink = () => isAdminLoggedIn ? '/admin' : '/dashboard';
  const getDashboardLabel = () => {
    if (isAdminLoggedIn) return 'Admin Panel';
    return user?.name ? `Hi, ${user.name.split(' ')[0]}` : 'Dashboard';
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    logout();
    navigate('/', { replace: true });
    setTimeout(() => window.location.reload(), 100);
  };

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (location.state?.fromLogin === true && (isLoggedIn || isAdminLoggedIn)) {
      const t = setTimeout(() => navigate('/', { replace: true, state: {} }), 300);
      return () => clearTimeout(t);
    }
  }, [location.state, isLoggedIn, isAdminLoggedIn, navigate]);

  // ── Fetch first 3 houses — identical API call to BrowsePage ──────────────
  useEffect(() => {
    const fetchHouses = async () => {
      setHousesLoading(true);
      setHousesError(null);
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/houses?page=1&limit=3`);
        setFeaturedHouses(res.data.houses || []);
      } catch (err) {
        setHousesError('Failed to load houses. Please try again.');
        console.error(err);
      } finally {
        setHousesLoading(false);
      }
    };
    fetchHouses();
  }, []);

  // ── Exact same handleViewDetails as BrowsePage ────────────────────────────
  const handleViewDetails = (houseId) => {
    if (!user && !isAdminLoggedIn) {
      navigate('/login', { state: { from: `/house/${houseId}` } });
    } else {
      navigate(`/house/${houseId}`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchLocation.trim()) params.set('location', searchLocation.trim());
    if (searchBudget) params.set('maxRent', searchBudget);
    if (searchBedrooms) params.set('bedrooms', searchBedrooms);
    navigate(`/browse?${params.toString()}`);
  };

  // Calculator
  const firstRentNum = Number(firstRent) || 0;
  const subsequentRentNum = Number(subsequentRent) || 0;
  const firstYearPlan = calculateFirstYearPlan(firstRentNum);
  const verificationFee = firstRentNum >= 100000 ? firstYearPlan.verificationFee : 0;
  const renewalFee = 7000;
  const upfrontRent = firstYearPlan.upfront;
  const remaining = firstYearPlan.remaining;
  const totalUpfront = upfrontRent + verificationFee;
  // Always ceil to nearest whole naira — never show a fraction of a naira
  const monthlyFirstYear = firstYearPlan.monthly;
  const monthlySubsequent = calculateSubsequentYearMonthly(subsequentRentNum);
  const showSubsequentSection = subsequentRent.trim() !== '' && subsequentRentNum > 0;
  const handleClearCalculator = () => { setFirstRent(''); setSubsequentRent(''); };

  return (
    <>
      <SEOHead title="LiteRent | Rent Verified Homes with 30% Upfront in Ibadan" description="LiteRent helps eligible renters in Ibadan move into verified homes with 30% upfront and spread the balance over time." canonical="https://literent.com.ng/" />
    <main className="min-h-screen bg-[#fdfdfd] text-[#1a1a1a] flex flex-col">

      {/* ══ HEADER ══ */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#afc09e] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/Logo.jpg"
                alt="LiteRent Logo"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain"
                onError={(e) => { e.target.src = 'https://via.placeholder.com/180x60?text=LiteRent'; }}
              />
            </Link>

            <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
              <Link to="/" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Home</Link>
              <a href="#how-it-works" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">How It Works</a>
              <Link to="/browse" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">About Us</Link>
              <Link to="/contact" className="text-[#294c79] hover:text-[#1f3a5c] font-medium transition-colors">Contact Us</Link>
              <div className="flex items-center space-x-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()}>
                      <button className={`px-6 py-2.5 text-white rounded-full font-medium transition shadow-sm ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>
                        {getDashboardLabel()}
                      </button>
                    </Link>
                    <button onClick={handleLogout} className="px-6 py-2.5 border-2 border-red-500 text-red-600 rounded-full font-medium hover:bg-red-50 transition">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"><button className="px-6 py-2.5 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium hover:bg-[#294c79]/10 transition">Login</button></Link>
                    <Link to="/signup"><button className="px-6 py-2.5 bg-[#a5c250] text-white rounded-full font-medium hover:bg-[#8bb83a] shadow-sm transition">Get Started</button></Link>
                  </>
                )}
              </div>
            </nav>

            <button
              className="lg:hidden p-2 rounded-md text-[#294c79] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#294c79]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                {isMenuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#afc09e] py-6 px-5 animate-fadeIn">
            <div className="flex flex-col space-y-5">
              <Link to="/" className="text-[#294c79] hover:text-[#1f3a5c] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <a href="#how-it-works" className="text-[#294c79] hover:text-[#1f3a5c] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>How It Works</a>
              <Link to="/browse" className="text-[#294c79] hover:text-[#1f3a5c] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Browse Houses</Link>
              <Link to="/about" className="text-[#294c79] hover:text-[#1f3a5c] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>About Us</Link>
              <Link to="/contact" className="text-[#294c79] hover:text-[#1f3a5c] font-medium py-3 text-lg" onClick={() => setIsMenuOpen(false)}>Contact Us</Link>
              <div className="pt-6 border-t border-gray-200 flex flex-col space-y-4">
                {(isLoggedIn || isAdminLoggedIn) ? (
                  <>
                    <Link to={getDashboardLink()} onClick={() => setIsMenuOpen(false)}>
                      <button className={`w-full py-4 text-white rounded-full font-medium text-lg ${isAdminLoggedIn ? 'bg-red-600 hover:bg-red-700' : 'bg-[#294c79] hover:bg-[#1f3a5c]'}`}>{getDashboardLabel()}</button>
                    </Link>
                    <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full py-4 border-2 border-red-500 text-red-600 rounded-full font-medium text-lg hover:bg-red-50">Logout</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 border-2 border-[#294c79] text-[#294c79] rounded-full font-medium text-lg hover:bg-[#294c79]/10">Login</button></Link>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}><button className="w-full py-4 bg-[#a5c250] text-white rounded-full font-medium text-lg hover:bg-[#8bb83a]">Get Started</button></Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      

      {/* ══ HERO ══ */}
      <section className="flex-1 flex flex-col items-center justify-center text-center px-5 sm:px-8 py-16 sm:py-24 md:py-32">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#294c79] mb-6 leading-tight">
          Rent a home.<br className="sm:hidden" /> Pay gradually.
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[#1a1a1a]/80 max-w-xl sm:max-w-3xl mb-10 px-2">
          Get your keys with just 30% of your annual rent. LiteRent lets you move in with a low upfront payment. We handle the rest — verified homes, flexible installments, and peace of mind.
        </p>
        <Link to="/browse" className="bg-[#a5c250] text-white px-10 sm:px-14 py-5 sm:py-6 rounded-full text-lg sm:text-xl font-medium hover:bg-[#8bb83a] shadow-lg transition transform hover:scale-105 inline-block">
          Find a House
        </Link>
      </section>

      {/* ══ LAUNCH COUNTDOWN + WAITLIST BANNER ══ */}
      <section className="bg-gradient-to-br from-[#294c79] via-[#1e3d6b] to-[#0f2440] py-16 sm:py-20 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#a5c250]/10 rounded-full translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-white/5 rounded-full -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="relative max-w-3xl mx-auto text-center">
          {/* Pulsing live badge */}
          <div className="inline-flex items-center gap-2 bg-[#a5c250]/20 border border-[#a5c250]/40 text-[#c8e87a] text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#a5c250] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#a5c250]" />
            </span>
            LiteRent is Here
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
            Ibadan's smartest rental platform<br className="hidden sm:block" /> is here
          </h2>
          <p className="text-white/70 text-lg sm:text-xl max-w-xl mx-auto mb-10">
            Fill in the interest form and lets us know exactly where you'd like to live  and your budget.
          </p>

          {/* Countdown clock */}
          {!countdown.launched ? (
            <div className="flex justify-center gap-4 sm:gap-8 mb-10">
              <CountdownBox value={countdown.days}    label="Days"    />
              <CountdownBox value={countdown.hours}   label="Hours"   />
              <CountdownBox value={countdown.minutes} label="Minutes" />
              <CountdownBox value={countdown.seconds} label="Seconds" />
            </div>
          ) : (
            <p className="text-[#a5c250] text-2xl font-bold mb-10">🎉 LiteRent is Here!</p>
          )}

          {/* Single clear CTA */}
          <Link
            to="/waitlist"
            className="inline-block bg-[#a5c250] hover:bg-[#8bb83a] text-white font-bold px-14 py-4 rounded-full text-xl transition shadow-lg shadow-[#a5c250]/30 active:scale-95"
          >
            Let us know your preferences →
          </Link>
          <p className="text-white/40 text-sm mt-4">Free to join • Priority spots available • Be first in line</p>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section id="how-it-works" className="py-16 sm:py-20 md:py-24 px-5 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#294c79] mb-12 sm:mb-16 md:mb-20">
            How LiteRent Works
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {[
              { emoji: "🔍", title: "1. Find & Choose", text: "Browse our curated, fully verified homes. Pick one that matches your needs and budget." },
              { emoji: "✅", title: "2. Get Verified and Onboarded", text: "Submit your details and documents. We verify everything quickly so you can proceed with confidence." },
              { emoji: "💰", title: "3. Low Upfront, Pay Gradually", text: "Make a reduced upfront payment (30%). Spread the rest into affordable monthly installments." },
              { emoji: "🏡", title: "4. Move In & Enjoy", text: "Get your keys, secure lease, and enjoy full support from LiteRent throughout your stay." },
            ].map((item, i) => (
              <div key={i} className="text-center px-5 sm:px-6 py-8 sm:py-10 bg-white rounded-3xl shadow-md hover:shadow-xl transition-all border border-[#e8f2d1]/50">
                <div className="w-20 sm:w-24 h-20 sm:h-24 mx-auto mb-6 rounded-full bg-[#a5c250]/10 flex items-center justify-center text-5xl sm:text-6xl">
                  {item.emoji}
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#294c79] mb-4 sm:mb-5">{item.title}</h3>
                <p className="text-[#1a1a1a]/80 text-base sm:text-lg leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 sm:mt-16 flex justify-center">
            <Link to="/how-it-works" className="bg-[#294c79] text-white px-10 py-4 rounded-full text-lg font-medium hover:bg-[#1f3a5c] shadow-md transition transform hover:scale-105 inline-block">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* ══ SEARCH ══ */}
      <section className="py-12 sm:py-16 bg-[#e8f2d1]/50">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#294c79] mb-8">Find your perfect home</h2>
          <form onSubmit={handleSearch} className="bg-white rounded-3xl shadow-xl p-6 sm:p-10 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              <div>
                <label className="block text-sm font-medium text-[#afc09e] mb-3">Location</label>
                <input
                  type="text"
                  placeholder="e.g., Akobo, Challenge, Lekki…"
                  className="w-full px-5 py-4 border border-[#afc09e] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-lg"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#afc09e] mb-3">Budget (Annual)</label>
                <select
                  className="w-full px-5 py-4 border border-[#afc09e] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-lg"
                  value={searchBudget}
                  onChange={(e) => setSearchBudget(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="500000-1500000">₦100,000 - ₦500,000</option>
                  <option value="1500000-3000000">₦500,000 - ₦3,000,000</option>
                  <option value="3000000-5000000">₦3,000,000 - ₦5,000,000</option>
                  <option value="5000000+">₦5,000,000+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#afc09e] mb-3">Bedrooms</label>
                <select
                  className="w-full px-5 py-4 border border-[#afc09e] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#a5c250] text-lg"
                  value={searchBedrooms}
                  onChange={(e) => setSearchBedrooms(e.target.value)}
                >
                  <option value="">Any</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5+">5+</option>
                </select>
              </div>
            </div>
            <button type="submit" className="mt-8 w-full bg-[#294c79] text-white py-5 rounded-xl font-medium text-xl hover:bg-[#1f3a5c] transition">
              Search Houses
            </button>
          </form>
        </div>
      </section>

      {/* ══ FEATURED HOUSES — exact same card as BrowsePage ══ */}
      <section id="featured-houses" className="py-16 sm:py-20 md:py-24 px-5 sm:px-6 lg:px-8 bg-[#fdfdfd]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#294c79] mb-12 sm:mb-16">
            Featured Verified Homes
          </h2>

          {/* Loading — same spinner as BrowsePage */}
          {housesLoading && (
            <BrandLoadingScreen fullScreen={false} message="Loading featured verified homes for you…" />
          )}

          {/* Error — same style as BrowsePage */}
          {housesError && !housesLoading && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl text-center">
              {housesError}
            </div>
          )}

          {/* Grid — exact same card markup as BrowsePage, capped at 3 */}
          {!housesLoading && !housesError && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {featuredHouses.length === 0 ? (
                  <p className="col-span-full text-center text-xl text-[#1a1a1a]/70 py-20">
                    No houses found matching your filters.
                  </p>
                ) : (
                  featuredHouses.map((house) => (
                    <div
                      key={house._id || house.id}
                      className="block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-[#e8f2d1] group"
                    >
                      {/* Image — copied exactly from BrowsePage */}
                      <div className="relative h-64 bg-gray-200">
                        {house.images && house.images.length > 0 ? (
                          <img
                            src={house.images[0]}
                            alt={house.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xl">
                            No Photo
                          </div>
                        )}
                            {house.status && (
                            <div className={`absolute top-4 right-4 text-white text-xs font-bold px-3 py-1 rounded-full ${
                              house.status === 'verified' ? 'bg-[#a5c250]' :
                              house.status === 'pending' ? 'bg-amber-500' :
                              house.status === 'sample' ? 'bg-violet-500' : 'bg-gray-400'
                            }`}>
                              {house.status.charAt(0).toUpperCase() + house.status.slice(1)}
                            </div>
                          )}
                      </div>

                      {/* Body — copied exactly from BrowsePage */}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-[#294c79] mb-2 group-hover:text-[#1f3a5c] transition">
                          {house.title || 'Untitled House'}
                        </h3>
                        <p className="text-[#1a1a1a]/80 mb-4">
                          {house.location} • {house.bedrooms} Bed{house.bedrooms !== 1 ? 's' : ''}
                        </p>
                        <p className="text-lg font-bold text-[#294c79] mb-4">
                          ₦{house.annual_rent ? parseFloat(house.annual_rent).toLocaleString() : '---'} / year
                        </p>
                        <div className="flex justify-between items-center">
                          <button
                            onClick={() => handleViewDetails(house._id || house.id)}
                            className="text-[#a5c250] font-medium hover:text-[#8bb83a] transition"
                          >
                            View Details →
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Browse all CTA */}
              {featuredHouses.length > 0 && (
                <div className="flex justify-center">
                  <Link
                    to="/browse"
                    className="bg-[#294c79] text-white px-12 py-4 rounded-full text-lg font-medium hover:bg-[#1f3a5c] shadow-md transition transform hover:scale-105 inline-block"
                  >
                    Browse All Homes
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ══ RENT CALCULATOR ══ */}
      <section className="py-16 sm:py-20 px-5 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#294c79]">Rent Calculator</h2>
            {(firstRent || subsequentRent) && (
              <button onClick={handleClearCalculator} className="bg-red-100 text-red-700 px-5 py-2 rounded-full text-sm font-medium hover:bg-red-200 transition">
                Clear
              </button>
            )}
          </div>
          <p className="text-center text-lg text-[#1a1a1a]/80 mb-8 sm:mb-10">
            See your upfront payment and monthly installments for the first year and beyond.
          </p>
          <div className="bg-[#fdfdfd] rounded-2xl shadow-lg p-6 sm:p-8 md:p-10">
            <label className="block text-base font-medium text-[#294c79] mb-3">First Year Annual Rent (₦)</label>
            <input
              type="number" placeholder="e.g., 4500000" min="100000"
              className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl text-xl focus:outline-none focus:border-[#a5c250] focus:ring-2 focus:ring-[#a5c250]/40 mb-8"
              value={firstRent} onChange={(e) => setFirstRent(e.target.value)}
            />
            <label className="block text-base font-medium text-[#294c79] mb-3">Subsequent Years Annual Rent (₦) – optional</label>
            <input
              type="number" placeholder="e.g., 4800000 (leave blank to skip)" min="0"
              className="w-full px-5 py-4 border-2 border-[#afc09e] rounded-xl text-xl focus:outline-none focus:border-[#a5c250] focus:ring-2 focus:ring-[#a5c250]/40"
              value={subsequentRent} onChange={(e) => setSubsequentRent(e.target.value)}
            />

            {firstRentNum >= 100000 && (
              <div className="mt-10 space-y-10 text-base">
                <div className="bg-white p-6 rounded-xl border border-[#e8f2d1]">
                  <h3 className="text-xl font-semibold text-[#294c79] mb-6 text-center">First Year</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Verification + Onboarding Fee</p>
                      <p className="font-bold text-[#294c79] text-xl">₦{verificationFee.toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">30% of Rent</p>
                      <p className="font-bold text-[#294c79] text-xl">₦{upfrontRent.toLocaleString()}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Total Upfront</p>
                      <p className="text-2xl font-bold text-[#a5c250]">₦{totalUpfront.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
                    <div>
                      <p className="text-sm text-gray-600">Remaining Balance (70%)</p>
                      <p className="font-bold text-[#294c79] text-xl">₦{remaining.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Monthly Payment (11 months)</p>
                      <p className="text-2xl font-bold text-[#a5c250]">₦{monthlyFirstYear.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">Includes 4.8% monthly interest on remaining balance</p>
                    </div>
                  </div>
                </div>
                {showSubsequentSection && (
                  <div className="bg-white p-6 rounded-xl border border-[#e8f2d1]">
                    <h3 className="text-xl font-semibold text-[#294c79] mb-6 text-center">Subsequent Years</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
                      <div>
                        <p className="text-sm text-gray-600">Renewal Fee</p>
                        <p className="font-bold text-[#294c79] text-xl">₦{renewalFee.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Monthly Payment (12 months)</p>
                        <p className="text-2xl font-bold text-[#a5c250]">₦{monthlySubsequent.toLocaleString()}</p>
                        <p className="text-xs text-gray-500 mt-1">Includes 4.4% monthly interest on annual rent</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {firstRent && firstRentNum > 0 && firstRentNum < 100000 && (
              <p className="text-center text-red-600 mt-6 text-base font-medium">
                Please enter a realistic annual rent (minimum ₦100,000).
              </p>
            )}
          </div>
        </div>
      </section>


      <Footer />

    </main>
      </>
  );
}
